import { Outlet, NavLink } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, Users, CreditCard, DollarSign, Activity,
  Mail, ToggleRight, BarChart3, Settings, LogOut, ChevronLeft,
  FileText, Bell, Menu, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    document.documentElement.classList.add("dark");
    return () => document.documentElement.classList.remove("dark");
  }, []);

  const navItems = [
    { to: "/admin", icon: LayoutDashboard, label: t("admin.dashboard"), end: true },
    { to: "/admin/customers", icon: Users, label: t("admin.customers") },
    { to: "/admin/subscriptions", icon: CreditCard, label: t("admin.subscriptions") },
    { to: "/admin/pricing", icon: DollarSign, label: t("admin.pricing") },
    { to: "/admin/payment-logs", icon: FileText, label: t("admin.payment_logs_title") },
    { to: "/admin/email-templates", icon: Mail, label: t("admin.email_templates") },
    { to: "/admin/affiliates", icon: Users, label: t("admin.affiliates_title") },
    { to: "/admin/invoices", icon: FileText, label: t("admin.invoices") },
    { to: "/admin/faq", icon: Activity, label: t("admin.faq_title") },
    { to: "/admin/appsumo", icon: DollarSign, label: t("admin.appsumo_title") },
    { to: "/admin/scripts", icon: Activity, label: t("admin.scripts_title") },
    { to: "/admin/translations", icon: Mail, label: t("admin.translations_title") },
    { to: "/admin/settings", icon: Settings, label: t("admin.settings") },
  ];

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-layout">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-[240px] bg-card border-r border-border flex flex-col transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-5 border-b border-border">
          <NavLink to="/admin" className="flex items-center gap-2" data-testid="admin-sidebar-logo">
            <div className="w-8 h-8 rounded-md bg-red-600 flex items-center justify-center">
              <span className="text-white font-bold text-sm">SP</span>
            </div>
            <div>
              <span className="font-bold text-lg tracking-tight">Smartpriced</span>
              <span className="ml-2 text-xs text-red-400 font-semibold">ADMIN</span>
            </div>
          </NavLink>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium ${
                  isActive ? "active" : "text-muted-foreground hover:text-foreground"
                }`
              }
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon className="w-4 h-4 shrink-0" />
              {item.label}
            </NavLink>
          ))}

          <div className="pt-4 pb-2 px-3">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t("nav.admin")}</span>
          </div>
          <NavLink
            to="/dashboard"
            className="sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground"
            data-testid="admin-nav-user-panel"
            onClick={() => setSidebarOpen(false)}
          >
            <ChevronLeft className="w-4 h-4" />
            {t("nav.dashboard")}
          </NavLink>
        </nav>

        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 text-sm font-bold">
              {user?.name?.[0]?.toUpperCase() || "A"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.role}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="w-full justify-start text-muted-foreground" onClick={logout} data-testid="admin-logout-button">
            <LogOut className="w-4 h-4 mr-2" />
            {t("nav.logout")}
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border flex items-center justify-between px-4 lg:px-6 bg-card/50 backdrop-blur-sm sticky top-0 z-30">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="admin-mobile-menu">
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
          <div className="flex-1" />
          <LanguageSwitcher />
        </header>
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
